import { reactive, computed } from 'vue';
import type { RequestContent } from '../types';
import { EXPIRATION_OPTIONS } from '../constants';

interface FormData {
  name: string;
  message: string;
  customId: string;
  expiresOption: number | null;
  collectionId: string | null;
  collectLogs: boolean;
}

interface UseRequestContentModalFormOptions {
  edit: boolean;
  item?: RequestContent | null;
  domain: string;
}

export const useRequestContentModalForm = (options: UseRequestContentModalFormOptions) => {
  const { edit, item } = options;

  // Expiration options from constants
  const expirationOptions = EXPIRATION_OPTIONS;

  const calculateHoursFromExpiresAt = (expiresAt?: string): number | null => {
    if (!expiresAt) {
      return null;
    }

    const now = new Date();
    const expirationDate = new Date(expiresAt);
    const diffInMs = expirationDate.getTime() - now.getTime();

    if (diffInMs <= 0) {
      return null; // Already expired
    }

    const diffInHours = Math.ceil(diffInMs / (1000 * 60 * 60));

    // Find the closest expiration option
    const validHours = [1, 24, 168, 720];
    const closest = validHours.reduce((prev, curr) => (Math.abs(curr - diffInHours) < Math.abs(prev - diffInHours) ? curr : prev));

    return closest;
  };

  // Note: computeExpirationDate removed as it was unused

  const createFormDataFromItem = (requestContentItem: RequestContent): FormData => ({
    name: requestContentItem.name,
    message: requestContentItem.message || '',
    customId: requestContentItem.customId || '',
    expiresOption: calculateHoursFromExpiresAt(requestContentItem.expiresAt),
    collectionId: requestContentItem.collectionId || null,
    collectLogs: requestContentItem.collectLogs || false,
  });

  const createDefaultFormData = (): FormData => ({
    name: '',
    message: '',
    customId: '',
    expiresOption: null,
    collectionId: null,
    collectLogs: false,
  });

  const formData = reactive<FormData>(edit && item ? createFormDataFromItem(item) : createDefaultFormData());

  // Computed properties for better form management
  const submitButtonText = computed(() => `${edit ? 'Update' : 'Create'} Request Link`);

  // Custom validation for required name (either user input or default)
  const isNameValid = computed(() => {
    return Boolean(formData.name?.trim() || 'Request Content');
  });

  // Note: Form dirty state is now handled by FormKit's state.dirty

  // Handle form submission with data cleaning
  const handleSubmit = (submittedData: any, getCollectionIdForSaving: () => string | null, getSystemDefaultName: () => string, organizationSettings: any) => {
    const cleanData = { ...submittedData };

    // Use organization defaults when name or message are empty
    if (!cleanData.name?.trim()) {
      cleanData.name = organizationSettings?.defaultTitle || getSystemDefaultName();
    }

    if (!cleanData.message?.trim()) {
      cleanData.message = organizationSettings?.defaultDescription || '';
    }

    // Remove customId if empty
    if (!cleanData.customId?.trim()) {
      delete cleanData.customId;
    }

    // Convert expiresOption to expiresAt timestamp or remove if not set
    if (cleanData.expiresOption) {
      const expirationDate = new Date();
      expirationDate.setHours(expirationDate.getHours() + cleanData.expiresOption);
      cleanData.expiresAt = expirationDate.toISOString();
    }

    delete cleanData.expiresOption;

    // Set collectionId from composable
    const collectionId = getCollectionIdForSaving();
    if (collectionId) {
      cleanData.collectionId = collectionId;
    } else {
      delete cleanData.collectionId;
    }

    // Remove UI-only field
    delete cleanData.collectionSelection;

    return cleanData;
  };

  return {
    formData,
    expirationOptions,
    submitButtonText,
    isNameValid,
    handleSubmit,
  };
};
